import type { DuplicateClientMatch, DuplicateMatchField } from '@/types/clientsApiTypes';

export type DuplicateHighlightField = DuplicateMatchField | 'both';

export interface DuplicateMatchSectionLabel {
  prefix: string;
  value?: string;
}

export interface DuplicateMatchSection {
  id: string;
  label: DuplicateMatchSectionLabel;
  clients: DuplicateClientMatch[];
  highlightField: DuplicateHighlightField;
}

export interface DuplicateWarningContent {
  title: string;
  description: string;
  sections: DuplicateMatchSection[];
}

const SINGLE_MATCH_TITLE = 'Possible duplicate client found';
const MULTIPLE_MATCH_TITLE = 'Possible duplicate clients found';

const SINGLE_MATCH_DESCRIPTION =
  'We found an existing client profile that matches the name or email you entered. Please review it before creating a new client.';
const MULTIPLE_MATCH_DESCRIPTION =
  'We found existing client profiles that match the name or email you entered. Please review them before creating a new client.';

function getUniqueClientCount(matches: DuplicateClientMatch[]): number {
  return new Set(matches.map((match) => match.id)).size;
}

function hasMatchField(match: DuplicateClientMatch, field: DuplicateMatchField): boolean {
  return match.match_reasons.includes(field);
}

function getSingleMatchLabel(
  match: DuplicateClientMatch,
  searchedEmail: string,
  searchedFullName: string
): DuplicateMatchSectionLabel {
  const matchesEmail = hasMatchField(match, 'email');
  const matchesName = hasMatchField(match, 'name');

  if (matchesEmail && matchesName) {
    return {
      prefix: match.match_label || 'Matched by Name and Email',
    };
  }

  if (matchesEmail) {
    return {
      prefix: 'Match found by:',
      value: searchedEmail,
    };
  }

  return {
    prefix: 'Match found by:',
    value: searchedFullName,
  };
}

function getSingleMatchHighlightField(match: DuplicateClientMatch): DuplicateHighlightField {
  const matchesEmail = hasMatchField(match, 'email');
  const matchesName = hasMatchField(match, 'name');

  if (matchesEmail && matchesName) {
    return 'both';
  }

  return matchesEmail ? 'email' : 'name';
}

function buildGroupedSections(
  matches: DuplicateClientMatch[],
  searchedEmail: string,
  searchedFullName: string
): DuplicateMatchSection[] {
  const bothMatches = matches.filter((match) => hasMatchField(match, 'email') && hasMatchField(match, 'name'));
  const emailOnlyMatches = matches.filter((match) => hasMatchField(match, 'email') && !hasMatchField(match, 'name'));
  const nameOnlyMatches = matches.filter((match) => hasMatchField(match, 'name') && !hasMatchField(match, 'email'));
  const sections: DuplicateMatchSection[] = [];

  if (bothMatches.length > 0) {
    sections.push({
      id: 'both',
      label: {
        prefix: 'Matched by Name and Email',
      },
      clients: bothMatches,
      highlightField: 'both',
    });
  }

  if (emailOnlyMatches.length > 0) {
    sections.push({
      id: 'email',
      label: {
        prefix: 'Match found by email:',
        value: `"${searchedEmail}"`,
      },
      clients: emailOnlyMatches,
      highlightField: 'email',
    });
  }

  if (nameOnlyMatches.length > 0) {
    sections.push({
      id: 'name',
      label: {
        prefix: 'Match found by name:',
        value: `"${searchedFullName}"`,
      },
      clients: nameOnlyMatches,
      highlightField: 'name',
    });
  }

  return sections;
}

export function getDuplicateWarningContent(
  matches: DuplicateClientMatch[],
  searchedEmail: string,
  searchedFullName: string
): DuplicateWarningContent {
  const uniqueClientCount = getUniqueClientCount(matches);
  const isSingleMatch = uniqueClientCount === 1;

  if (isSingleMatch) {
    const match = matches[0];

    return {
      title: SINGLE_MATCH_TITLE,
      description: SINGLE_MATCH_DESCRIPTION,
      sections: [
        {
          id: 'single',
          label: getSingleMatchLabel(match, searchedEmail, searchedFullName),
          clients: [match],
          highlightField: getSingleMatchHighlightField(match),
        },
      ],
    };
  }

  return {
    title: MULTIPLE_MATCH_TITLE,
    description: MULTIPLE_MATCH_DESCRIPTION,
    sections: buildGroupedSections(matches, searchedEmail, searchedFullName),
  };
}
